/* eslint-disable max-lines */
import { filter } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import * as commonActions from 'common/actions'
import {
  BEST_EGG_DEMAND_COMPANY_UUID,
  BEST_EGG_SUPPLY_COMPANY_UUID,
  CONVERSION_PROBABILITY_SORTED_COMPANY_UUIDS,
  LoanOfferProductSubType,
  MultiProductDispatch,
} from 'embed/constants'
import Parent from 'common/services/Parent'
import { LoanOffer, SpecialOffer } from 'embed/constants/models'
import {
  AprVsRecSorting,
  DoNotRedirectPilotPartners,
  Experiment,
  Original,
  ShowAllOffersOnResultsStep,
  SortByClosestLoanAmount,
  Variant,
  withExperiment,
} from 'embed/experiments'
import { MultiProductState } from 'embed/reducers'
import { groupOffersByLenderId, sortLoanOffers } from 'embed/utils/sort-offers'

import {
  closestLoanAmountOffersSelector,
  conversionProbabilityLoanOffersSelector,
  getPreferredOfferPartnerSelector,
  isLoanResultsViewSelector,
  lowestAprLoanOffersSelector,
  offerStyleIsCompactSelector,
  ratesFilteredLoanOffersSelector,
  recommendedLoanOffersSelector,
  setShouldAutoRedirectSelector,
} from 'embed/selectors'
import { loanEmailSelector } from 'embed/selectors/application'
import { isLoggedInSelector } from 'embed/selectors/auth'
import {
  configAppPurposeSelector,
  configCompanyUuidSelector,
  configResultsUuidSelector,
  configStepSelector,
} from 'embed/selectors/config'
import { loadingSelector } from 'embed/selectors/loading'
import {
  loanAmountStateSelector,
  loanOffersSelector,
  loanRatesFiltersSelector,
  loanRatesLeadUuidSelector,
  loanRatesSortDirectionSelector,
  loanRatesSortOptionSelector,
  loanRatesUuidSelector,
  loanSpecialOffersSelector,
} from 'embed/selectors/rates'
import Session from 'common/services/Session'
import * as actions from 'embed/actions'
import { uiPreviewIsInitializingSelector } from 'embed/selectors/ui'
import {
  LenderIdKeys,
  LoanPurposeOptions,
  LoanSortOption,
  LoanStep,
  MANDATORY_REDIRECT_SPECIAL_OFFER_PARTNER_NAME,
  OfferType,
  Product,
  RedirectType,
  SortDirection,
} from 'even-ts-static'
import { DisplayOfferType, LoanResultsView } from './index'
import { filtersLoanLenderIdSelector, filtersLoanPurposeSelector } from 'embed/selectors/filters'
import { usePrevious } from 'embed/hooks'
import { filterUniqueLoanOffersByCompanyUuid } from 'common/services/OfferFilters'

// Hardcoded Map until Preferred Offer Partner becomes a more first class feature
const hardcodedMap = require('./offer-partners.json')

interface OwnProps {
  selectedAprVsRecSortingVariantId?: string
  selectedSortByClosestLoanAmountVariantId?: string
  selectedShowAllOffersOnResultsStepVariantId?: string
}

interface StoreProps {
  appPurpose: LoanPurposeOptions | undefined
  bestOffer: LoanOffer | SpecialOffer
  displayOfferType: DisplayOfferType
  email: string
  filteredLenderId: string
  isCompactResults?: boolean
  isLoggedIn: boolean
  isResultsView: boolean
  loading: boolean
  loanOffers: LoanOffer[]
  loanOffersByLender: { [s: string]: LoanOffer[] }
  loanPurpose: LoanPurposeOptions | undefined
  mandatoryRedirectSpecialOffer?: SpecialOffer
  preferredOffer?: LoanOffer
  preferredOfferPartner?: string
  preferredOfferPartnerName?: string
  rateTableLeadUuid: string
  rateTableUuid: string
  redirectTimerStarted: boolean
  selectedAprVsRecSortingVariantId?: string
  selectedSortByClosestLoanAmountVariantId?: string
  shouldRedirect?: boolean
  specialOffers: SpecialOffer[]
  configuredStep?: string
}

interface DispatchProps {
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => void
  dispatchSortBy: (sortDirection: SortDirection, sortOption: LoanSortOption) => void
  dispatchGetLoanOffer: (offer: LoanOffer, redirectType: RedirectType) => void
  dispatchGetSpecialOffer: (offer: SpecialOffer, redirectType: RedirectType) => void
  dispatchUpdateTags: () => void
}

type Props = StoreProps & DispatchProps

// This function lets us distinguish between loan offers and special offers. It won't work generally, because credit
// card offers can have `preApproved` and `preQualified` properties. But it will do in the present context.
function isLoanOffer(arg: LoanOffer | SpecialOffer): arg is LoanOffer {
  return arg.hasOwnProperty('preApproved') && arg.hasOwnProperty('preQualified')
}

const getPreferredOfferPartnerName = (preferredOfferPartner: string) =>
  hardcodedMap[preferredOfferPartner]?.displayName ?? undefined

const moveOfferToTop = (offer: LoanOffer, offers: LoanOffer[]) => [
  offer,
  ...offers.filter((x: LoanOffer) => x.uuid !== offer.uuid),
]

export const ResultsContainer: React.FC<Props> = ({
  bestOffer,
  configuredStep,
  dispatchAppendRateTableUuidToParentUrl,
  dispatchGetLoanOffer,
  dispatchGetSpecialOffer,
  dispatchSortBy,
  dispatchUpdateTags,
  loanPurpose,
  loanOffers,
  mandatoryRedirectSpecialOffer,
  preferredOffer,
  rateTableLeadUuid,
  rateTableUuid,
  selectedAprVsRecSortingVariantId,
  selectedSortByClosestLoanAmountVariantId,
  ...props
}) => {
  const onAprVsRecSortingExperimentActivated = React.useCallback(() => {
    // Once the experiment is activated set the correct sort
    switch (selectedAprVsRecSortingVariantId) {
      case AprVsRecSorting.variants.Apr.id:
        dispatchSortBy(SortDirection.Asc, LoanSortOption.Apr)
        break
      case AprVsRecSorting.variants.ConversionProbability.id:
        dispatchSortBy(SortDirection.Desc, LoanSortOption.ConversionProbability)
        break
      default:
        dispatchSortBy(SortDirection.Desc, LoanSortOption.Recommended)
    }

    // Also update the lead to ensure the experiment variant is updated
    dispatchUpdateTags()
  }, [selectedAprVsRecSortingVariantId, dispatchSortBy])

  React.useEffect(() => {
    // Sort Loan results by APR if purpose is Student Loan Finance, otherwise rec score
    if (loanPurpose === LoanPurposeOptions.STUDENT_REFINANCE) {
      dispatchSortBy(SortDirection.Asc, LoanSortOption.Apr)
    } else {
      if (selectedAprVsRecSortingVariantId !== undefined) {
        onAprVsRecSortingExperimentActivated()
      } else {
        // If we're not in the AprVsRecSortingExperiment, default to LoanSortOption.Recommended sorting
        dispatchSortBy(SortDirection.Desc, LoanSortOption.Recommended)
      }
    }

    dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
  }, [])

  const prevRateTableLeadUuid = usePrevious(rateTableLeadUuid)
  const prevRateTableUuid = usePrevious(rateTableUuid)
  const prevSelectedAprVsRecSortingVariantId = usePrevious(selectedAprVsRecSortingVariantId)
  const prevSelectedSortByClosestLoanAmountVariantId = usePrevious(selectedSortByClosestLoanAmountVariantId)

  const onSortByClosestLoanAmountExperimentActivated = React.useCallback(() => {
    if (selectedSortByClosestLoanAmountVariantId === SortByClosestLoanAmount.variants.SortByClosestAmount.id) {
      dispatchSortBy(SortDirection.Asc, LoanSortOption.ClosestAmount)
    } else {
      dispatchSortBy(SortDirection.Desc, LoanSortOption.Recommended)
    }

    dispatchUpdateTags()
  }, [selectedSortByClosestLoanAmountVariantId, dispatchSortBy, dispatchUpdateTags])

  const onTimerComplete = React.useCallback(() => {
    if (preferredOffer) {
      return dispatchGetLoanOffer(preferredOffer, RedirectType.AUTO)
    } else if (mandatoryRedirectSpecialOffer !== undefined) {
      return dispatchGetSpecialOffer(mandatoryRedirectSpecialOffer, RedirectType.AUTO)
    } else if (isLoanOffer(bestOffer)) {
      return dispatchGetLoanOffer(bestOffer, RedirectType.AUTO)
    } else {
      return dispatchGetSpecialOffer(bestOffer, RedirectType.AUTO)
    }
  }, [bestOffer, dispatchGetLoanOffer, dispatchGetSpecialOffer, mandatoryRedirectSpecialOffer, preferredOffer])

  React.useEffect(() => {
    if (rateTableUuid && rateTableUuid !== prevRateTableUuid) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      Parent.publishMessage('rate_table_uuid', { rateTableUuid })
    }

    if (configuredStep === LoanStep.Results && rateTableLeadUuid && rateTableLeadUuid !== prevRateTableLeadUuid) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      const { leadUuidHasChanged } = Session.forceOverrideLeadUuid(rateTableLeadUuid)
      if (leadUuidHasChanged) {
        dispatchUpdateTags()
      }
    }

    // The process of activating the experiment is async, so we check for the experiment
    // during both the component mount and update phase in order to ensure we're sorting
    // correctly if the experiment is activated before or after mount
    if (prevSelectedAprVsRecSortingVariantId === undefined && selectedAprVsRecSortingVariantId !== undefined) {
      onAprVsRecSortingExperimentActivated()
    }
    if (
      prevSelectedSortByClosestLoanAmountVariantId === undefined &&
      selectedSortByClosestLoanAmountVariantId !== undefined
    ) {
      onSortByClosestLoanAmountExperimentActivated()
    }
  }, [
    configuredStep,
    prevRateTableLeadUuid,
    prevRateTableUuid,
    prevSelectedAprVsRecSortingVariantId,
    prevSelectedSortByClosestLoanAmountVariantId,
    rateTableLeadUuid,
    rateTableUuid,
    selectedAprVsRecSortingVariantId,
    selectedSortByClosestLoanAmountVariantId,
  ])

  const hasPreapprovedOrPrequalifiedOffers: undefined | LoanOffer = loanOffers.find(
    (offer: LoanOffer) => offer.preApproved || offer.preQualified,
  )

  if (hasPreapprovedOrPrequalifiedOffers) {
    return (
      <Experiment experiment={DoNotRedirectPilotPartners}>
        <Original>
          <LoanResultsView
            onTimerComplete={onTimerComplete}
            {...props}
            bestOffer={bestOffer}
            dispatchGetLoanOffer={dispatchGetLoanOffer}
            preferredOffer={preferredOffer}
            loanOffers={loanOffers}
          />
        </Original>
        <Variant experiment={DoNotRedirectPilotPartners} variant={DoNotRedirectPilotPartners.variants.doNotRedirect}>
          <LoanResultsView
            onTimerComplete={onTimerComplete}
            {...props}
            shouldRedirect={false}
            bestOffer={bestOffer}
            dispatchGetLoanOffer={dispatchGetLoanOffer}
            preferredOffer={preferredOffer}
            loanOffers={loanOffers}
          />
        </Variant>
      </Experiment>
    )
  } else {
    return (
      <LoanResultsView
        onTimerComplete={onTimerComplete}
        {...props}
        bestOffer={bestOffer}
        dispatchGetLoanOffer={dispatchGetLoanOffer}
        preferredOffer={preferredOffer}
        loanOffers={loanOffers}
      />
    )
  }
}

// Filters offers based on lender display logic
const getResultingOffers = (
  isFilteringByLender,
  loanOffersByLender,
  filteredLenderId,
  isResultsView,
  recommendedOffers,
  loanOffers,
  selectedShowAllOffersOnResultsStepVariantId,
) => {
  if (isFilteringByLender) {
    return loanOffersByLender[filteredLenderId]
  } else if (isResultsView) {
    // Show only one offer per lender
    if (selectedShowAllOffersOnResultsStepVariantId === ShowAllOffersOnResultsStep.variants.ShowAllOffers.id) {
      return recommendedOffers
    } else {
      const uniqueOffers = filterUniqueLoanOffersByCompanyUuid(recommendedOffers)
      return filter(loanOffers, (offer: LoanOffer) => uniqueOffers.includes(offer))
    }
  } else {
    // Show only one offer per lender
    const uniqueOffers = filterUniqueLoanOffersByCompanyUuid(recommendedOffers)
    return filter(loanOffers, (offer: LoanOffer) => uniqueOffers.slice(0, 3).includes(offer))
  }
}

const getRecommendedOffers = (
  state,
  selectedAprVsRecSortingVariantId,
  selectedSortByClosestLoanAmountVariantId,
  useConversionProbabilityToSort,
  ratesFilters,
) => {
  let offers
  switch (selectedAprVsRecSortingVariantId) {
    case AprVsRecSorting.variants.Apr.id:
      offers = lowestAprLoanOffersSelector(state)
      break
    case AprVsRecSorting.variants.ConversionProbability.id:
      offers = conversionProbabilityLoanOffersSelector(state)
      break
    default:
      offers = recommendedLoanOffersSelector(state)
  }
  if (selectedSortByClosestLoanAmountVariantId === SortByClosestLoanAmount.variants.SortByClosestAmount.id) {
    offers = closestLoanAmountOffersSelector(state)
  }
  if (useConversionProbabilityToSort) {
    offers = conversionProbabilityLoanOffersSelector(state)
  }
  offers = ratesFilteredLoanOffersSelector(offers, ratesFilters)
  return offers
}

const filterBySlrAndAprType = (offers, ratesFilters) => {
  let filteredOffers
  if (ratesFilters.productSubType?.[0] === LoanOfferProductSubType.StudentLoanRefinance) {
    filteredOffers = ratesFilteredLoanOffersSelector(offers, ratesFilters)
  }
  return filteredOffers ?? offers
}

const appendBBBRating = (loanOffers: LoanOffer[]) =>
  loanOffers?.map((offer) => {
    const companyUuid = offer?.originator?.companyUuid
    if (offer.originator && companyUuid !== undefined && hardcodedMap[companyUuid] !== undefined) {
      offer.originator.bbbRating = hardcodedMap[companyUuid].bbbRating
    }
    return offer
  })

const isOfferBestEggPreApprovedOffer = (offer) =>
  offer?.originator?.companyUuid === BEST_EGG_DEMAND_COMPANY_UUID && offer.preApproved

// Find the highest placed best egg offer
const getBestEggsPreApprovedOffer = (companyUuid, offers): LoanOffer | undefined => {
  if (companyUuid === BEST_EGG_SUPPLY_COMPANY_UUID) {
    return offers.find((offer: LoanOffer) => isOfferBestEggPreApprovedOffer(offer))
  }
}

const isMandatoryRedirectSpecialOffer = (specialOffers, displayOfferType) =>
  displayOfferType === DisplayOfferType.Special &&
  specialOffers.length > 0 &&
  specialOffers[0].partnerName === MANDATORY_REDIRECT_SPECIAL_OFFER_PARTNER_NAME

const isSortRecommendedDescending = (sortOption: LoanSortOption, sortDirection: SortDirection) =>
  sortOption === LoanSortOption.Recommended && sortDirection === SortDirection.Desc

const getOfferIndex = (offers, companyUuid) =>
  offers.findIndex((offer) => offer.originator?.companyUuid === companyUuid)

const getOffer = (offers, index) => offers.find((offer, i) => i === index)

const filterOffers = (offers, index) => offers.filter((offer, i) => i !== index)

export const mapStateToProps = (state: MultiProductState, ownProps: OwnProps = {}): StoreProps => {
  const companyUuid = configCompanyUuidSelector(state)
  /**
   * Per https://evenfinancial.atlassian.net/browse/V1-1238 , for some company_uuids we want
   * to sort by conversion probability instead of recommendation score
   */
  const useConversionProbabilityToSort = CONVERSION_PROBABILITY_SORTED_COMPANY_UUIDS.includes(companyUuid || '')
  const ratesFilters = loanRatesFiltersSelector(state)
  const recommendedOffers = getRecommendedOffers(
    state,
    ownProps.selectedAprVsRecSortingVariantId,
    ownProps.selectedSortByClosestLoanAmountVariantId,
    useConversionProbabilityToSort,
    ratesFilters,
  )
  const loanOffersByLender = groupOffersByLenderId(recommendedOffers, LenderIdKeys[Product.Loan])
  const isResultsView = isLoanResultsViewSelector(state)
  const filteredLenderId = filtersLoanLenderIdSelector(state)
  const isFilteringByLender = filteredLenderId !== undefined

  // Determine which offers to show
  const loanOffers = filterBySlrAndAprType(loanOffersSelector(state), ratesFilters)
  let resultingOffers: LoanOffer[] = getResultingOffers(
    isFilteringByLender,
    loanOffersByLender,
    filteredLenderId,
    isResultsView,
    recommendedOffers,
    loanOffers,
    ownProps.selectedShowAllOffersOnResultsStepVariantId,
  )
  resultingOffers = appendBBBRating(resultingOffers)

  // Sort by selected any selected sort toggles
  const sortOption = loanRatesSortOptionSelector(state)
  const sortDirection = loanRatesSortDirectionSelector(state)
  const loanAmount = loanAmountStateSelector(state)
  // TODO: Improve typing of sortLoanOffers and sortOffers so that this type assertion isn't necessary anymore
  let sortedOffers = sortLoanOffers(
    resultingOffers,
    sortOption,
    sortDirection,
    loanAmount,
    useConversionProbabilityToSort,
  ) as LoanOffer[]
  const specialOffers = loanSpecialOffersSelector(state)
  let bestOffer = recommendedOffers.length > 0 ? recommendedOffers[0] : specialOffers[0]

  /* BEGIN BEST EGG HACK */

  // for best egg supply subaccount, if there's a preApproved best egg offer
  // artificially prioritize it in offer list and on auto redirect
  bestOffer = getBestEggsPreApprovedOffer(companyUuid, recommendedOffers) ?? bestOffer
  if (isOfferBestEggPreApprovedOffer(bestOffer) && isSortRecommendedDescending(sortOption, sortDirection)) {
    sortedOffers = moveOfferToTop(bestOffer, sortedOffers)
  }

  /* END BEST EGG HACK */

  // An extra-special type of special offer that is guaranteed to redirect
  let shouldRedirect =
    isLoanResultsViewSelector(state) && setShouldAutoRedirectSelector(state) && !configResultsUuidSelector(state)
  let mandatoryRedirectSpecialOffer: SpecialOffer | undefined
  const displayOfferType = sortedOffers.length > 0 ? DisplayOfferType.Loan : DisplayOfferType.Special
  if (isMandatoryRedirectSpecialOffer(specialOffers, displayOfferType)) {
    shouldRedirect = true
    mandatoryRedirectSpecialOffer = specialOffers[0]
  }

  // Get offer from a preferred offer partner (if available)
  let preferredOffer
  const preferredOfferPartner = getPreferredOfferPartnerSelector(state)
  const preferredOfferPartnerName = getPreferredOfferPartnerName(preferredOfferPartner || '')
  if (preferredOfferPartnerName) {
    const preferredOfferIndex = getOfferIndex(sortedOffers, preferredOfferPartner)
    if (preferredOfferIndex !== -1) {
      preferredOffer = getOffer(sortedOffers, preferredOfferIndex)
      sortedOffers = filterOffers(sortedOffers, preferredOfferIndex)
    }
  }

  const redirectTimerStarted = !loadingSelector(state, Product.Loan)
  const rateTableLeadUuid = loanRatesLeadUuidSelector(state)
  const rateTableUuid = loanRatesUuidSelector(state)
  const configuredStep = configStepSelector(state)
  const loanPurpose = filtersLoanPurposeSelector(state)
  const isCompactResults = offerStyleIsCompactSelector(state)
  const selectedAprVsRecSortingVariantId = ownProps.selectedAprVsRecSortingVariantId
  const selectedSortByClosestLoanAmountVariantId = ownProps.selectedSortByClosestLoanAmountVariantId
  const email = loanEmailSelector(state) as string
  const isLoggedIn = isLoggedInSelector(state)
  const appPurpose = configAppPurposeSelector(state)
  const loading = loadingSelector(state, Product.Loan) || uiPreviewIsInitializingSelector(state)

  return {
    appPurpose,
    bestOffer,
    configuredStep,
    displayOfferType,
    email,
    filteredLenderId,
    isCompactResults,
    isLoggedIn,
    isResultsView,
    loading,
    loanOffers: sortedOffers,
    loanOffersByLender,
    loanPurpose,
    mandatoryRedirectSpecialOffer,
    preferredOffer,
    preferredOfferPartner,
    preferredOfferPartnerName,
    rateTableLeadUuid,
    rateTableUuid,
    redirectTimerStarted,
    selectedAprVsRecSortingVariantId,
    selectedSortByClosestLoanAmountVariantId,
    shouldRedirect,
    specialOffers,
  }
}

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => dispatch(actions.appendUuidToParentUrl(uuid)),
  dispatchGetLoanOffer: (offer: LoanOffer, redirectType: RedirectType) =>
    dispatch(commonActions.redirectToOffer(offer, OfferType.Loan, redirectType)),
  dispatchGetSpecialOffer: (offer: SpecialOffer, redirectType: RedirectType) =>
    dispatch(commonActions.redirectToOffer(offer, OfferType.Special, redirectType)),
  dispatchSortBy: (sortDirection: SortDirection, sortOption: LoanSortOption) =>
    dispatch(commonActions.sortChangedAction(sortDirection, Product.Loan, sortOption)),
  dispatchUpdateTags: () => dispatch(commonActions.updateTags(Product.Loan)),
})

const connectedContainer = connect(mapStateToProps, mapDispatchToProps)(ResultsContainer)

export const LoanResultsContainer = withExperiment(AprVsRecSorting, {
  selectedVariantPropName: 'selectedAprVsRecSortingVariantId',
})(
  withExperiment(SortByClosestLoanAmount, {
    selectedVariantPropName: 'selectedSortByClosestLoanAmountVariantId',
  })(
    withExperiment(ShowAllOffersOnResultsStep, {
      selectedVariantPropName: 'selectedShowAllOffersOnResultsStepVariantId',
    })(connectedContainer),
  ),
)
